import { abbreviateNumber } from 'js-abbreviation-number'
import { Settings as SettingsIcon } from 'lucide-react'
import { Bar, BarChart, XAxis } from 'recharts'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@components/chart'
import { Loader } from '@components/v2/loader'
import { cn } from '@global/utils/tailwind'

const Header = ({ children, className }) => {
  return <div className={className}>{children}</div>
}

const Title = ({ children, className = '' }) => {
  return (
    <h1 className={cn('text-cv-blue-400 text-sm font-medium flex items-center', className)}>
      {children}
    </h1>
  )
}

const Button = ({ active, onClick, label, count, isLoading = false }) => {
  return (
    <button
      data-active={active}
      className="relative z-30 flex flex-1 flex-col justify-center border-t text-left even:border-l data-[active=true]:bg-cv-blue-150 sm:border-l sm:border-t-0 sm:px-[9px] sm:py-[9px]"
      onClick={onClick}
    >
      <span className="text-sm text-muted-foreground text-cv-blue-400 mb-0">{label}</span>
      <span className="text-lg font-bold leading-none sm:text-[26px] text-cv-blue-700">
        {isLoading ? (
          <div className="p-2">
            <Loader />
          </div>
        ) : (
          abbreviateNumber(count)
        )}
      </span>
    </button>
  )
}

const SettingsButton = ({ onClick }) => {
  return (
    <button className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t text-left even:border-l sm:border-l sm:border-t-0 sm:pl-[20px] sm:py-[15px]">
      <SettingsIcon size={24} color="var(--clvn-blue-400)" onClick={onClick} />
    </button>
  )
}

const Content = ({ children }) => {
  return <div className="p-5">{children}</div>
}

/*
  Custom shape function that only applies minPointSize for non-zero values
*/
const customBarShape = props => {
  const { x, y, width, height, value } = props

  // For zero values, return an invisible rect
  if (value === 0 || value === '0') {
    return <rect x={x} y={y} width={width} height={0} fill="transparent" />
  }

  // For non-zero values, apply minimum height if needed
  const minHeight = 5
  const finalHeight = height < minHeight && height > 0 ? minHeight : height
  const finalY = height < minHeight && height > 0 ? y - (minHeight - height) : y

  return <rect x={x} y={finalY} width={width} height={finalHeight} fill={props.fill} />
}

const Chart = ({ config, data, activeChart }) => {
  return (
    <ChartContainer config={config} className="aspect-auto h-[250px] w-full px-[60px]">
      <BarChart accessibilityLayer data={data}>
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={value => {
            const date = new Date(value)
            return date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
          }}
          tick={{ fill: 'var(--clvn-blue-700)', fontSize: 11 }}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              className="w-[150px] bg-white"
              labelFormatter={value => {
                const date = new Date(value)
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })
              }}
            />
          }
        />
        {activeChart === 'all' ? (
          <>
            <ChartLegend content={<ChartLegendContent className="uppercase text-cv-blue-400" />} />
            {Object.keys(config).map(key => (
              <Bar
                key={key}
                dataKey={key}
                stackId="a"
                fill={config[key].color}
                shape={customBarShape}
              />
            ))}
          </>
        ) : (
          <Bar
            dataKey={activeChart}
            fill={config[activeChart].color}
            stackId="a"
            shape={customBarShape}
          />
        )}
      </BarChart>
    </ChartContainer>
  )
}

export const Card = ({ variant, children, className = '' }) => {
  return (
    <div
      className={cn(`border border-cv-blue-200 rounded-md ${className}`, {
        'bg-white': variant === 'primary',
        'bg-cv-blue-100': variant === 'secondary',
      })}
      style={{
        boxShadow: `0px 2px 8px 0px rgba(0, 0, 0, 0.1)`,
      }}
    >
      {children}
    </div>
  )
}

Card.Header = Header
Card.Title = Title
Card.HeaderButton = Button
Card.SettingsButton = SettingsButton
Card.Content = Content
Card.Chart = Chart
